import {
  TABLE_DELETE,
  bindingComponentType,
  isShowLoadingAction,
  qrCodeStatus,
} from '@common/constants/shared';
import { showQrCodeSelector } from '@common/redux/selectors/page';
import {
  resetQrCodeScanner,
  showQrCodeScanner,
} from '@common/redux/slice/page';
import ActionWrapper from '@common/screens/LoadingAction';
import { IOnPressProps } from '@common/types/action';
import { ActionTypes, ElementType } from '@common/types/index';
import { getSourceDataBinding } from '@common/utils/binding';
import {
  checkingTypeComponent,
  convertParamAction,
  getFlattedMetadata,
} from '@common/utils/component';
import { actionPromise } from '@common/utils/handleActions/excuteAction';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { find, get, some, isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ListObject from './Components/ListObject';
import SimpleObject from './Components/SimpleObject';
import DependenciesConnector from './DependenciesConnnector';

type Props = {
  ObjectClass: FC;
  obj: ElementType;
  dependencies: any;
};

const BindingComponent = React.memo(
  ({ ObjectClass, obj, dependencies }: Props) => {
    const dispatch = useDispatch();
    const { qrCode, status } = useSelector(showQrCodeSelector);
    const { messages } = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [params, setParams] = useState<any>(null);

    const onShowQrCode = useCallback(
      (params: Record<string, any>) => {
        dispatch(showQrCodeScanner(true));
        setParams(params);
      },
      [dispatch]
    );

    const showLoadingAction = isShowLoadingAction.includes(obj.componentName);

    const handleTableDelete = useCallback(
      async (actionId: string, options: IOnPressProps) => {
        return actionPromise(
          convertParamAction({
            obj,
            dependencies,
            options,
            objectId: obj.id,
            actionId,
            messages,
            arrayAction: [],
          })
        ).then((data) => {
          setLoading(false);
          return data;
        });
      },
      [dependencies, messages, obj]
    );

    const onPress = useCallback(
      async (actionId: string | null, options: IOnPressProps) => {
        try {
          if (actionId === TABLE_DELETE) {
            return await handleTableDelete(actionId, options);
          }
          if (!actionId || typeof actionId !== 'string') return;
          const metadata = getFlattedMetadata(obj.screenUuid);
          const actionObject = find(metadata, (item) =>
            JSON.stringify(item).includes(actionId)
          );
          if (!actionObject) return;
          if (showLoadingAction) setLoading(true);
          const objectId = actionObject.id;
          const arrayAction = get(obj, `actions.${actionId}.actions`, []);

          const params = convertParamAction({
            obj,
            dependencies,
            options,
            objectId,
            actionId,
            messages,
            arrayAction,
          });

          const result = await actionPromise(params);

          const includeQrCodeAction =
            Array.isArray(result) &&
            some(result, {
              actionType: ActionTypes.QR_CODE_SCANNER,
            });

          if (!includeQrCodeAction) {
            return result;
          } else {
            onShowQrCode(params);
          }
        } catch (error) {
          console.log('error', error);
        } finally {
          if (showLoadingAction) setLoading(false);
        }
      },
      [dependencies, messages, obj, showLoadingAction, onShowQrCode]
    );

    useEffect(() => {
      const action = async () => {
        if (qrCodeStatus.includes(status) && params) {
          await actionPromise({ ...params, scannerId: qrCode });
          setParams(null);
          dispatch(resetQrCodeScanner());
        }
      };

      action();
    }, [dispatch, params, qrCode, status]);

    const dataBinding = getSourceDataBinding(obj);

    const props = useMemo(
      () => ({ ObjectClass, obj, onPress, dependencies }),
      [ObjectClass, obj, onPress, dependencies]
    );

    const componentType =
      checkingTypeComponent(obj) || bindingComponentType.SIMPLE;

    const loadingAction = showLoadingAction && loading;

    if (loadingAction) {
      return <ActionWrapper obj={obj} />;
    }

    if (componentType === bindingComponentType.MULTIPLE) {
      return <ListObject {...props} />;
    }

    return <SimpleObject {...props} />;
  },
  isEqual
);

export default DependenciesConnector(BindingComponent);
